import React, { Component, memo } from 'react';
import { WithTranslation, withTranslation, Trans } from 'react-i18next';
import styled from 'styled-components/macro';

import { colors, distances, grid } from '../../../styles/constants';
import { LoadingOverlay } from '../../../components/Loading';
import { H2, P } from '../../../components/Typography';
import { State as AccountState } from '../reducer';
import { AccountIdsProps, withAccountIds } from '../../../auth/accessToken/withAccountIds';
import AccountStep from './AccountStep';
import ConnectionsStep from './ConnectionsStep';
import ApiClientStep from './ApiClientStep';
import { Account, Client, Grant } from '../../../types/management-auth';
import getConnections from '../../../helpers/getConnections';
import StyledLink from '../../../components/StyledLink';

export interface CheckoutWizardPageProps extends WithTranslation, AccountIdsProps {
    getAccount: (accountId: string) => void;
    continueWhilePending: (accountId: string) => void;
    account: AccountState;
}

const getProgress = (
    accountIds: string[],
    account: Account | undefined,
    clients: Client[],
    grantsByClientId: { [client_id: string]: Grant[] }
) => {
    if (!account) {
        return 0;
    }
    if (accountIds.length === 1) {
        return 1;
    }
    const numEnabledConnections = getConnections(account, 'enabled').length;

    if (numEnabledConnections === 0) {
        return 2;
    }
    if (!hasCheckoutClient(account, clients, grantsByClientId)) {
        return 3;
    }
    return 4;
};

const checkoutGrant = (grant: Grant): boolean => {
    return (
        grant.scope.indexOf('reqular') > -1 ||
        (grant.scope.indexOf('read:checkout') > -1 && grant.scope.indexOf('write:checkout') > -1) ||
        grant.scope.indexOf('admin:checkout') > -1
    );
};

const hasCheckoutClient = (
    account: Account,
    clients: Client[],
    grantsByClientId: { [client_id: string]: Grant[] }
): boolean => {
    const activeClients = clients.filter((c) => !c.deleted_at);
    const activeClientGrants = activeClients.reduce((acc: Grant[], c) => {
        const grants = grantsByClientId[c.client_id || ''] || [];
        return [
            ...acc,
            ...grants.filter((g) => {
                if (g.deleted_at) {
                    return false;
                }
                // has production audience
                return g.audience.includes(`P${account.account_id}`);
            }),
        ];
    }, []);

    return activeClientGrants.some(checkoutGrant);
};

class CheckoutWizard extends Component<CheckoutWizardPageProps> {
    componentDidMount() {
        const accountId = this.props.prodAccountId || this.props.urlAccountId;
        this.props.getAccount(accountId);
    }

    render() {
        const { t, account,  accountIds } = this.props;

        const current = getProgress(
            accountIds,
            account.account,
            account.clients,
            account.grantsByClientId
        );

        return (
            <Wizard>
                {current < 4 ? (
                    <H2>{t('wizards.checkout.title')}</H2>
                ) : (
                    <Finished>{t('wizards.checkout.title_finished')}</Finished>
                )}
                <br />
                <br />
                {current === 4 && (
                    <P>
                        <Trans i18nKey="wizards.checkout.finished_more_info">
                            <StyledLink href="https://docs.dintero.com/docs/getting-started/">
                                in-store payments or on-line payments
                            </StyledLink>
                        </Trans>
                        <br />
                        <br />
                    </P>
                )}
                {!account.isLoading && account.account && (
                    <div>
                        <AccountStep current={current} />
                        <ConnectionsStep
                            current={current}
                        />
                        <ApiClientStep current={current} />
                    </div>
                )}

                {account.isLoading && <LoadingOverlay />}
            </Wizard>
        );
    }
}

const Wizard = styled.div`
    max-width: ${grid.spans.span7};
    margin: 0 auto 100px;
    padding: ${distances.normal};
    position: relative;
    min-height: calc(${distances.ginormous} + ${distances.large});
    border: 2px solid ${colors.borderLight};
`;

const Finished = styled(H2)`
    color: ${colors.text};
    display: inline-block;
`;

export default withAccountIds(withTranslation()(memo(CheckoutWizard)));
